import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ChevronRight, Loader2 } from 'lucide-react';
import {
  useDeleteEntreprise,
  useEntreprises,
  useGroupes,
  useUpsertEntreprise,
  type UpsertEntrepriseInput,
} from '@/hooks/queries/useEntreprises';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { FormColorPicker } from '@/components/forms/FormColorPicker';
import { FormFileUpload } from '@/components/forms/FormFileUpload';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import { useRole } from '@/hooks/useRole';
import { t } from '@/locales/i18n';
import type { EntrepriseRow, FormeJuridiqueDz } from '@/lib/database.types';

const entrepriseSchema = z.object({
  groupe_id: z.string().uuid({ message: 'Groupe requis' }),
  nom: z.string().min(2, 'Min. 2 caractères'),
  forme_juridique: z.enum(['EURL', 'SARL', 'SPA', 'SNC', 'SCS', 'SCA', 'EI', 'Holding', 'Autre']),
  nif: z.string().optional().nullable(),
  rc: z.string().optional().nullable(),
  adresse: z.string().optional().nullable(),
  ville: z.string().optional().nullable(),
  telephone: z.string().optional().nullable(),
  email: z.string().email('E-mail invalide').optional().or(z.literal('')).nullable(),
  logo_url: z.string().optional().nullable(),
  couleur_accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Couleur hex requise'),
  is_demo: z.boolean(),
});

type EntrepriseForm = z.infer<typeof entrepriseSchema>;

const FORMES: FormeJuridiqueDz[] = [
  'EURL',
  'SARL',
  'SPA',
  'SNC',
  'SCS',
  'SCA',
  'EI',
  'Holding',
  'Autre',
];

export function OrganisationPage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: entreprises = [], isLoading } = useEntreprises();
  const { data: groupes = [] } = useGroupes();
  const upsert = useUpsertEntreprise();
  const remove = useDeleteEntreprise();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EntrepriseRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EntrepriseRow | null>(null);

  const openNew = (): void => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: EntrepriseRow): void => {
    setEditing(row);
    setDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('parametres.organisation.title')}
        subtitle={t('parametres.organisation.subtitle')}
        actions={
          isAdmin && (
            <Button onClick={openNew}>
              <Plus className="h-4 w-4" />
              {t('parametres.organisation.addEntreprise')}
            </Button>
          )
        }
      />

      <div className="space-y-3 p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </div>
        ) : entreprises.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Aucune entreprise.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {entreprises.map((e) => (
              <article
                key={e.id}
                className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
                style={{ borderLeftWidth: 4, borderLeftColor: e.couleur_accent }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {e.logo_url ? (
                      <img src={e.logo_url} alt="" className="h-10 w-10 rounded-md object-cover" />
                    ) : (
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-md text-sm font-bold text-white"
                        style={{ backgroundColor: e.couleur_accent }}
                      >
                        {e.nom.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3 className="text-sm font-semibold leading-tight">{e.nom}</h3>
                      <p className="text-xs text-muted-foreground">
                        {e.forme_juridique}
                        {e.ville ? ` · ${e.ville}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {e.is_demo && <Badge variant="secondary">{t('common.demo')}</Badge>}
                    {!e.is_active && <Badge variant="outline">{t('common.inactive')}</Badge>}
                  </div>
                </div>

                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <KV label="NIF" value={e.nif} />
                  <KV label="RC" value={e.rc} />
                  <KV label="Tél." value={e.telephone} />
                  <KV label="E-mail" value={e.email} />
                </dl>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/parametres/organisation/${e.id}/activites`}>
                      Activités <ChevronRight className="h-3 w-3" />
                    </Link>
                  </Button>
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(e)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('common.edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete(e)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {t('common.delete')}
                      </Button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <EntrepriseFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        groupes={groupes}
        onSubmit={async (values) => {
          await upsert.mutateAsync({ id: editing?.id, ...values } as UpsertEntrepriseInput);
          toast.success(editing ? 'Entreprise mise à jour' : 'Entreprise créée');
          setDialogOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer « ${confirmDelete?.nom} » ?`}
        description="Cette action supprime aussi toutes les activités, services et tiers liés. Irréversible."
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await remove.mutateAsync(confirmDelete.id);
            toast.success('Entreprise supprimée');
            setConfirmDelete(null);
          } catch (err) {
            toast.error('Suppression impossible : ' + (err as Error).message);
          }
        }}
      />
    </div>
  );
}

function KV({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`truncate ${value ? '' : 'italic text-muted-foreground/60'}`}>
        {value || 'À renseigner'}
      </dd>
    </div>
  );
}

interface EntrepriseFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: EntrepriseRow | null;
  groupes: { id: string; nom: string }[];
  onSubmit: (values: EntrepriseForm) => Promise<void>;
}

function EntrepriseFormDialog({
  open,
  onOpenChange,
  editing,
  groupes,
  onSubmit,
}: EntrepriseFormDialogProps): JSX.Element {
  const defaultGroupe = groupes[0]?.id ?? '';
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EntrepriseForm>({
    resolver: zodResolver(entrepriseSchema),
    defaultValues: {
      groupe_id: defaultGroupe,
      nom: '',
      forme_juridique: 'SARL',
      couleur_accent: '#6366F1',
      is_demo: false,
    },
  });

  useEffect(() => {
    if (open) {
      reset(
        editing
          ? {
              groupe_id: editing.groupe_id,
              nom: editing.nom,
              forme_juridique: editing.forme_juridique,
              nif: editing.nif,
              rc: editing.rc,
              adresse: editing.adresse,
              ville: editing.ville,
              telephone: editing.telephone,
              email: editing.email,
              logo_url: editing.logo_url,
              couleur_accent: editing.couleur_accent,
              is_demo: editing.is_demo,
            }
          : {
              groupe_id: defaultGroupe,
              nom: '',
              forme_juridique: 'SARL',
              couleur_accent: '#6366F1',
              is_demo: false,
            },
      );
    }
  }, [open, editing, defaultGroupe, reset]);

  const couleur = watch('couleur_accent');
  const logo = watch('logo_url');
  const groupeOptions = useMemo(
    () => groupes.map((g) => ({ value: g.id, label: g.nom })),
    [groupes],
  );

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={
        editing
          ? t('parametres.organisation.editEntreprise')
          : t('parametres.organisation.addEntreprise')
      }
      description="Les champs marqués d’un astérisque sont obligatoires."
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        await onSubmit(v);
      })}
    >
      <FormSection title="Identité">
        <FormField
          label={t('parametres.organisation.form.nom')}
          required
          {...register('nom')}
          error={errors.nom?.message}
          fullWidth
        />
        <FormSelect
          label={t('parametres.organisation.form.groupe')}
          required
          value={watch('groupe_id')}
          onChange={(v) => setValue('groupe_id', v, { shouldValidate: true })}
          options={groupeOptions}
          error={errors.groupe_id?.message}
        />
        <FormSelect
          label={t('parametres.organisation.form.forme')}
          required
          value={watch('forme_juridique')}
          onChange={(v) =>
            setValue('forme_juridique', v as FormeJuridiqueDz, { shouldValidate: true })
          }
          options={FORMES.map((f) => ({ value: f, label: f }))}
        />
        <FormField
          label={t('parametres.organisation.form.nif')}
          {...register('nif')}
          placeholder="À renseigner"
        />
        <FormField
          label={t('parametres.organisation.form.rc')}
          {...register('rc')}
          placeholder="À renseigner"
        />
      </FormSection>

      <FormSection title="Contact">
        <FormField label={t('parametres.organisation.form.ville')} {...register('ville')} />
        <FormField label={t('parametres.organisation.form.telephone')} {...register('telephone')} />
        <FormField
          type="email"
          label={t('parametres.organisation.form.email')}
          {...register('email')}
          error={errors.email?.message}
        />
        <FormTextarea
          label={t('parametres.organisation.form.adresse')}
          {...register('adresse')}
          fullWidth
        />
      </FormSection>

      <FormSection title="Identité visuelle">
        <FormColorPicker
          label={t('parametres.organisation.form.couleur')}
          value={couleur}
          onChange={(v) => setValue('couleur_accent', v, { shouldValidate: true })}
          error={errors.couleur_accent?.message}
          fullWidth
        />
        <FormFileUpload
          label={t('parametres.organisation.form.logo')}
          bucket="avatars"
          folder="logos-entreprises"
          accept="image/*"
          maxBytes={2 * 1024 * 1024}
          value={logo ?? null}
          onChange={(v) => setValue('logo_url', v)}
          fullWidth
          hint="PNG / JPG / SVG ≤ 2 MB"
        />
        <FormCheckbox
          label={t('parametres.organisation.form.isDemo')}
          hint={t('parametres.organisation.form.isDemoHint')}
          {...register('is_demo')}
          fullWidth
        />
      </FormSection>
    </FormDialog>
  );
}
